import { useForm } from '@inertiajs/react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { FormationProposedName } from '@/types'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  formationId: string
  legalName: string
  proposedNames: FormationProposedName[]
}

// A formation has a first choice (Formation#legal_name) plus two backups —
// staff pick which one actually cleared. Picking a backup promotes it: the
// server swaps it into legal_name so every other part of the app (filing,
// certificate, mailers) uses the name that won.
export function NameClearanceDialog({ open, onOpenChange, formationId, legalName, proposedNames }: Props) {
  // Nested explicitly rather than left to Rails' param-wrapping — see
  // RegisteredAgentAddressDialog for why relying on that inference is fragile.
  const { data, setData, post, processing, errors, reset, clearErrors } = useForm({ name_clearance: { proposed_name_id: '' } })

  function handleClose(success: boolean) {
    onOpenChange(false)
    if (!success) {
      reset()
      clearErrors()
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post(`/backoffice/formations/${formationId}/name_clearance`, {
      onSuccess: () => handleClose(true)
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full sm:max-w-md overflow-y-auto">
        <DialogHeader className="mb-6">
          <DialogTitle>Clear a name</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 px-4">
          {errors['name_clearance.proposed_name_id'] && <p role="alert" className="text-sm text-destructive">{errors['name_clearance.proposed_name_id']}</p>}

          <RadioGroup
            value={data.name_clearance.proposed_name_id || '__primary__'}
            onValueChange={(value) => setData('name_clearance', { proposed_name_id: value === '__primary__' ? '' : value })}
            aria-label="Which name cleared?"
          >
            <label
              htmlFor="name-clearance-primary"
              className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors has-data-[state=checked]:border-foreground has-data-[state=checked]:bg-muted"
            >
              <RadioGroupItem value="__primary__" id="name-clearance-primary" />
              <span className="text-sm text-foreground">
                {legalName} <span className="text-xs text-muted-foreground">(first choice)</span>
              </span>
            </label>
            {proposedNames.map((proposedName) => (
              <label
                key={proposedName.id}
                htmlFor={`name-clearance-${proposedName.id}`}
                className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors has-data-[state=checked]:border-foreground has-data-[state=checked]:bg-muted"
              >
                <RadioGroupItem value={proposedName.id} id={`name-clearance-${proposedName.id}`} />
                <span className="text-sm text-foreground">
                  {proposedName.name} <span className="text-xs text-muted-foreground">(backup)</span>
                </span>
              </label>
            ))}
          </RadioGroup>

          <p className="text-xs text-muted-foreground">
            Choosing a backup promotes it — it becomes this formation's legal name everywhere in the app.
          </p>

          <div className="sticky bottom-0 bg-background border-t p-4 -mx-4 mt-8">
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={processing}>
                {processing ? 'Clearing...' : 'Clear name'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
